// src/pages/CRM/SFA/CategoryManagement.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, IconButton, TextField,
  FormControl, InputLabel, Select, MenuItem, Stack,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Alert, CircularProgress, Breadcrumbs, Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { productService } from './sfaService';

const CategoryManagement = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState({ categories: [], subcategories: [], product_families: [] });
  const [newCategory, setNewCategory] = useState({ name: '', description: '', parent_category_id: null });
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'Category Management | Sales Force Automation';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadCategories();
    }
  }, [canView]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await productService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load categories', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      enqueueSnackbar('Category name is required', { variant: 'error' });
      return;
    }

    try {
      await productService.createCategory(newCategory);
      enqueueSnackbar('Category created', { variant: 'success' });
      setNewCategory({ name: '', description: '', parent_category_id: null });
      loadCategories();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to create category', { variant: 'error' });
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      await productService.updateCategory(editingCategory.id, {
        name: editingCategory.name,
        description: editingCategory.description
      });
      enqueueSnackbar('Category updated', { variant: 'success' });
      setEditingCategory(null);
      loadCategories();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to update category', { variant: 'error' });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await productService.deleteCategory(categoryId);
      enqueueSnackbar('Category deleted', { variant: 'success' });
      setDeleteConfirm(null);
      loadCategories();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to delete category', { variant: 'error' });
    }
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        You don't have permission to view categories.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate(-1)}
          >
            Products
          </Link>
          <Typography color="text.primary">Category Management</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Category Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Organize products with categories and subcategories
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to Products
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label="Categories" icon={<CategoryIcon />} iconPosition="start" />
          <Tab label="Subcategories" icon={<DashboardIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Categories Tab */}
          {tab === 0 && (
            <Paper sx={{ p: 3 }}>
              {canModify && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Add New Category
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Category Name"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Description (Optional)"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    />
                    <Button variant="contained" onClick={handleCreateCategory} startIcon={<AddIcon />}>
                      Add Category
                    </Button>
                  </Stack>
                </Box>
              )}

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Existing Categories ({categories.categories?.length || 0})
              </Typography>
              <List>
                {categories.categories?.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <CategoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">No categories yet</Typography>
                    {canModify && (
                      <Typography variant="caption" color="text.secondary">
                        Create your first category above
                      </Typography>
                    )}
                  </Box>
                ) : (
                  categories.categories?.map((cat) => (
                    <ListItem
                      key={cat.id}
                      divider
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      {editingCategory?.id === cat.id ? (
                        <Box sx={{ flexGrow: 1 }}>
                          <Stack spacing={1}>
                            <TextField
                              fullWidth
                              size="small"
                              value={editingCategory.name}
                              onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              value={editingCategory.description || ''}
                              onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                              placeholder="Description"
                            />
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="contained" onClick={handleUpdateCategory}>
                                Save
                              </Button>
                              <Button size="small" onClick={() => setEditingCategory(null)}>
                                Cancel
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ) : (
                        <>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={600}>
                                {cat.name}
                              </Typography>
                            }
                            secondary={
                              <>
                                {cat.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {cat.description}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="primary">
                                  {cat.product_count} product(s)
                                </Typography>
                              </>
                            }
                          />
                          {canModify && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setEditingCategory(cat)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setDeleteConfirm(cat)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </>
                      )}
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          )}

          {/* Subcategories Tab */}
          {tab === 1 && (
            <Paper sx={{ p: 3 }}>
              {canModify && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Add New Subcategory
                  </Typography>
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Parent Category</InputLabel>
                      <Select
                        value={newCategory.parent_category_id || ''}
                        label="Parent Category"
                        onChange={(e) => setNewCategory({ ...newCategory, parent_category_id: e.target.value })}
                      >
                        {categories.categories?.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      label="Subcategory Name"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Description (Optional)"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    />
                    <Button variant="contained" onClick={handleCreateCategory} startIcon={<AddIcon />}>
                      Add Subcategory
                    </Button>
                  </Stack>
                </Box>
              )}

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Existing Subcategories ({categories.subcategories?.length || 0})
              </Typography>
              <List>
                {categories.subcategories?.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <DashboardIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">No subcategories yet</Typography>
                    {canModify && (
                      <Typography variant="caption" color="text.secondary">
                        Create your first subcategory above
                      </Typography>
                    )}
                  </Box>
                ) : (
                  categories.subcategories?.map((sub) => {
                    const parentCategory = categories.categories?.find(c => c.id === sub.parent_category_id);
                    return (
                      <ListItem
                        key={sub.id}
                        divider
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        {editingCategory?.id === sub.id ? (
                          <Box sx={{ flexGrow: 1 }}>
                            <Stack spacing={1}>
                              <TextField
                                fullWidth
                                size="small"
                                value={editingCategory.name}
                                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                value={editingCategory.description || ''}
                                onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                                placeholder="Description"
                              />
                              <Stack direction="row" spacing={1}>
                                <Button size="small" variant="contained" onClick={handleUpdateCategory}>
                                  Save
                                </Button>
                                <Button size="small" onClick={() => setEditingCategory(null)}>
                                  Cancel
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>
                        ) : (
                          <>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {sub.name}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    Parent: {parentCategory?.name || 'Unknown'}
                                  </Typography>
                                  {sub.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {sub.description}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="primary">
                                    {sub.product_count} product(s)
                                  </Typography>
                                </>
                              }
                            />
                            {canModify && (
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => setEditingCategory(sub)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => setDeleteConfirm(sub)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            )}
                          </>
                        )}
                      </ListItem>
                    );
                  })
                )}
              </List>
            </Paper>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirm?.name}"?
          </Typography>
          {deleteConfirm?.product_count > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This category has {deleteConfirm.product_count} product(s) and cannot be deleted.
              Please reassign or delete the products first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDeleteCategory(deleteConfirm.id)}
            disabled={deleteConfirm?.product_count > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;